import { useState, type ReactNode, type RefObject } from 'react'
import { AlertCircle, BarChart3, Clock, Download, FileImage, FileSpreadsheet, RotateCw, Table2 } from 'lucide-react'
import { cn } from '../../../lib/cn'
import { Badge } from '../Badge'
import { Button } from '../Button'
import { DropdownButton } from '../DropdownButton'
import { EmptyState } from '../EmptyState'
import { isSessionExpiredMessage, SessionExpiredAction } from '../SessionExpiredAction'
import { SegmentedControl } from '../SegmentedControl'
import { Skeleton } from '../Skeleton'
import { Table, type Column } from '../Table'
import { cellText, downloadCsv, safeFileName, type ChartTableData } from './csv'
import type { EChartHandle } from './EChart'

export type ChartView = 'chart' | 'table'

/** Статус цифр: «оценка» — период ещё может измениться (источники дописывают задним числом), «финал» — закрыт. */
export type ChartDataStatus = 'estimate' | 'final'

export interface ChartCardEmpty {
  title: string
  description?: string
  icon?: ReactNode
  action?: { label: string; onClick: () => void; icon?: ReactNode }
}

export interface ChartCardProps {
  /** Заголовок — вопрос, на который отвечает график («Сколько заказали по дням?»). */
  title: string
  subtitle?: ReactNode
  /** Переключатели метрики/шага справа от заголовка. */
  actions?: ReactNode
  /** Строка над графиком: крошки провала, легенда шкалы. */
  toolbar?: ReactNode
  /** Свежесть данных: «Данные по 19.10.2026». */
  freshness?: string
  status?: ChartDataStatus | null
  /** Пояснение к статусу (в подсказке бейджа). */
  statusHint?: string
  /** Табличный вид: включает переключатель «График / Таблица» и выгрузку CSV. */
  table?: ChartTableData | null
  /** Ссылка на график для выгрузки PNG. */
  chartRef?: RefObject<EChartHandle | null>
  /** Имя файлов выгрузки (без расширения); по умолчанию — заголовок. */
  exportName?: string
  /** Период, фильтры и база показателей, сохраняемые вместе с выгрузкой. */
  exportContext?: string
  /** Запрет выгрузки сохранённого кадра при обновлении/ошибке родительского отчёта. */
  exportsDisabled?: boolean
  onTableCellClick?: (row: Record<string, unknown>, columnKey: string, rowIndex: number) => void
  isTableCellAction?: (row: Record<string, unknown>, columnKey: string, rowIndex: number) => boolean
  /** Управляемый вид; без него карточка помнит выбор сама. */
  view?: ChartView
  onViewChange?: (view: ChartView) => void
  /** Первая загрузка (данных ещё нет) — скелетон высотой `height`. Перезагрузку показывает сам график. */
  loading?: boolean
  /** Ошибка загрузки. */
  error?: string | null
  onRetry?: () => void
  /** Нет данных (это не «ноль»): причина и, если есть, действие. */
  empty?: ChartCardEmpty | null
  /** Высота области графика (для скелетона и пустого состояния). */
  height?: number
  /** Подсказка внизу: что можно сделать с графиком. */
  hint?: ReactNode
  className?: string
  children: ReactNode
}

const VIEW_OPTIONS = [
  { value: 'chart' as const, label: 'График', icon: <BarChart3 className="h-3.5 w-3.5" aria-hidden="true" /> },
  { value: 'table' as const, label: 'Таблица', icon: <Table2 className="h-3.5 w-3.5" aria-hidden="true" /> },
]

const MENU_ITEM =
  'flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:text-zinc-200 dark:hover:bg-zinc-800'

/** Таблица-двойник графика. */
export function ChartDataTable({ table, className, onTableCellClick, isTableCellAction }: {
  table: ChartTableData
  className?: string
} & Pick<ChartCardProps, 'onTableCellClick' | 'isTableCellAction'>) {
  const columns: Array<Column<Record<string, unknown>>> = table.columns.map((column) => ({
    key: column.key,
    header: column.label,
    align: column.align,
    render: (_value, row, index) => {
      const label = cellText(column, row)
      const className = cn('tabular-nums', column.mono && 'font-mono text-xs')
      return onTableCellClick && (!isTableCellAction || isTableCellAction(row, column.key, index)) ? (
        <button type="button" className={cn(className, 'rounded text-blue-600 underline decoration-dotted underline-offset-4 focus-visible:outline focus-visible:outline-2 dark:text-blue-400')}
          aria-label={`${column.label}: ${label} — открыть детализацию`}
          onClick={() => onTableCellClick(row, column.key, index)}>{label}</button>
      ) : <span className={className}>{label}</span>
    },
  }))
  return (
    <div className={cn('max-h-[28rem] overflow-y-auto', className)}>
      <Table<Record<string, unknown>>
        columns={columns}
        data={table.rows}
        keyExtractor={(_row, index) => index}
        compact
        emptyMessage="Нет данных"
      />
    </div>
  )
}

/**
 * Рамка графика: заголовок-вопрос, переключатели, «График / Таблица», выгрузка PNG/CSV, свежесть и статус
 * «оценка/финал», состояния «загрузка», «ошибка», «нет данных». «Нет данных» — отдельное состояние с причиной,
 * а не пустой график с нулями.
 */
export function ChartCard({
  title,
  subtitle,
  actions,
  toolbar,
  freshness,
  status,
  statusHint,
  table,
  chartRef,
  exportName,
  exportContext,
  exportsDisabled,
  onTableCellClick,
  isTableCellAction,
  view: controlledView,
  onViewChange,
  loading,
  error,
  onRetry,
  empty,
  height = 320,
  hint,
  className,
  children,
}: ChartCardProps) {
  const [ownView, setOwnView] = useState<ChartView>('chart')
  const [exportError, setExportError] = useState<string | null>(null)
  const view = controlledView ?? ownView
  const setView = (next: ChartView) => {
    if (onViewChange) onViewChange(next)
    if (controlledView === undefined) setOwnView(next)
  }
  const fileName = safeFileName(exportName ?? title)
  const exportAllowed = !loading && !error && !empty && !exportsDisabled
  const canExportPng = exportAllowed && Boolean(chartRef) && view === 'chart'
  const canExportCsv = exportAllowed && Boolean(table && table.rows.length > 0)
  const context = [title, exportContext, freshness, status === 'estimate' ? 'Оценка' : status === 'final' ? 'Финал' : null, statusHint].filter(Boolean).join(' · ')
  const showBody = !error && !empty && !loading

  return (
    <section
      className={cn(
        'rounded-lg border border-gray-200 bg-white shadow-soft dark:border-zinc-800 dark:bg-zinc-900',
        className,
      )}
      aria-label={title}
    >
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-200 px-5 py-3.5 dark:border-zinc-800">
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">{title}</h3>
          {subtitle && <div className="mt-0.5 text-xs text-gray-500 dark:text-zinc-400">{subtitle}</div>}
          {(freshness || status) && (
            <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-zinc-400">
              {freshness && (
                <span className="inline-flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                  {freshness}
                </span>
              )}
              {status === 'estimate' && (
                <span title={statusHint}>
                  <Badge variant="yellow">Оценка</Badge>
                </span>
              )}
              {status === 'final' && (
                <span title={statusHint}>
                  <Badge variant="green">Финал</Badge>
                </span>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {actions}
          {table && (
            <SegmentedControl<ChartView>
              aria-label="Вид"
              size="sm"
              options={VIEW_OPTIONS}
              value={view}
              onChange={setView}
            />
          )}
          {(chartRef || table) && (
            <DropdownButton
              size="sm"
              align="right"
              icon={<Download className="h-3.5 w-3.5" aria-hidden="true" />}
              title="Скачать"
              showChevron={false}
              disabled={!canExportPng && !canExportCsv}
              buttonAttrs={{ 'aria-label': 'Скачать' }}
              panelClassName="min-w-[180px]"
            >
              {(close) => (
                <>
                  {chartRef && (
                    <button
                      type="button"
                      role="menuitem"
                      className={MENU_ITEM}
                      disabled={!canExportPng}
                      onClick={async () => {
                        close()
                        setExportError(null)
                        try {
                          if (!chartRef.current) throw new Error('График ещё не готов')
                          await chartRef.current.exportPng(fileName, context)
                        }
                        catch { setExportError('Не удалось подготовить PNG. Попробуйте повторить выгрузку.') }
                      }}
                    >
                      <FileImage className="h-4 w-4" aria-hidden="true" />
                      Картинка PNG
                    </button>
                  )}
                  {table && (
                    <button
                      type="button"
                      role="menuitem"
                      className={MENU_ITEM}
                      disabled={!canExportCsv}
                      onClick={() => {
                        close()
                        downloadCsv(fileName, table, context)
                      }}
                    >
                      <FileSpreadsheet className="h-4 w-4" aria-hidden="true" />
                      Таблица CSV
                    </button>
                  )}
                </>
              )}
            </DropdownButton>
          )}
        </div>
      </header>

      {toolbar && <div className="border-b border-gray-100 px-5 py-2.5 dark:border-zinc-800">{toolbar}</div>}
      {exportError && <p role="alert" className="px-5 pt-3 text-sm text-red-700 dark:text-red-400">{exportError}</p>}

      <div className="px-5 py-4">
        {error ? (
          <div
            role="alert"
            className="flex flex-col items-center justify-center gap-3 text-center"
            style={{ minHeight: Math.min(height, 240) }}
          >
            <div className="flex items-center gap-2 text-sm text-red-700 dark:text-red-400">
              <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
              {error}
            </div>
            <div className="flex flex-wrap items-center justify-center gap-2">
              {isSessionExpiredMessage(error) && <SessionExpiredAction />}
              {onRetry && (
                <Button variant="secondary" size="sm" icon={<RotateCw className="h-3.5 w-3.5" />} onClick={onRetry}>
                  Повторить
                </Button>
              )}
            </div>
          </div>
        ) : loading ? (
          <Skeleton className="w-full" height={height} />
        ) : empty ? (
          <EmptyState
            icon={empty.icon}
            title={empty.title}
            description={empty.description}
            action={empty.action}
            className="py-10"
          />
        ) : null}

        {showBody && (view === 'table' && table ? <ChartDataTable table={table} onTableCellClick={onTableCellClick} isTableCellAction={isTableCellAction} /> : children)}
      </div>

      {hint && showBody && view === 'chart' && (
        <footer className="border-t border-gray-100 px-5 py-2 text-xs text-gray-500 dark:border-zinc-800 dark:text-zinc-400">
          {hint}
        </footer>
      )}
    </section>
  )
}

export default ChartCard
